import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { hasRole } from '@collector/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { displayPatientName, formatDateTime } from '../lib/format'
import { PageHeading } from '../components/PageHeading'

/**
 * Home (at `/`) is the landing page for every signed-in user. Reached by
 * clicking the product-name brand link in the header. Surfaces three
 * things:
 *   1. Decode Label — the operator's most common action, on every role.
 *   2. Pending tablet registrations — admin/developer only, and only
 *      when there actually are pending rows (silent otherwise).
 *   3. Recent data sets — a short glance-level feed for context.
 */
export function HomePage() {
  const auth = useAuth()
  const { t } = useTranslation()
  const isAdmin = hasRole(auth.user, 'admin', 'developer')
  // PII visibility tracks the server's mask, which keys on `admin` only.
  const canSeePii = hasRole(auth.user, 'admin')

  const dataSets = useQuery({
    queryKey: ['data-sets', 'recent'],
    queryFn: () => api().dataSets.list({ limit: 5 }),
  })
  const pendingTablets = useQuery({
    queryKey: ['tablets', 'pending'],
    queryFn: () => api().tablets.list('pending'),
    enabled: isAdmin,
  })
  const pendingCount = pendingTablets.data?.length ?? 0

  return (
    <div className="space-y-6">
      <PageHeading title={t('home.title')} description={t('home.welcome')} />

      <DecodeLabelCard />

      {isAdmin && pendingCount > 0 ? <PendingTabletsCard count={pendingCount} /> : null}

      <section className="card">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          {t('home.recentDataSets')}
        </h2>
        {dataSets.data && dataSets.data.items.length > 0 ? (
          <ul className="divide-y divide-stone-200 dark:divide-stone-700">
            {dataSets.data.items.map((item) => (
              <li key={item.id} className="py-2 text-sm">
                <Link
                  to={`/data-sets/${item.id}`}
                  className="font-medium text-stone-900 hover:underline dark:text-stone-100"
                >
                  {displayPatientName(item.patient, canSeePii)}
                </Link>{' '}
                ·{' '}
                <span className="text-stone-500 dark:text-stone-400">
                  {formatDateTime(item.capturedAt)}
                </span>{' '}
                · <span className="text-stone-500 dark:text-stone-400">{item.tabletName}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-stone-500 dark:text-stone-400">{t('home.noDataSets')}</p>
        )}
      </section>
    </div>
  )
}

function DecodeLabelCard() {
  const { t } = useTranslation()
  return (
    <Link
      to="/decode-label"
      className="flex items-center gap-4 rounded-lg border border-emerald-900 bg-emerald-800 p-6 text-emerald-50 shadow-sm transition hover:bg-emerald-900"
    >
      <div
        aria-hidden="true"
        className="flex h-14 w-14 flex-none items-center justify-center rounded-md bg-emerald-950/40"
      >
        <ScanQrIcon />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-lg font-semibold">{t('home.decodeLabelTitle')}</div>
        <div className="text-sm text-emerald-100/80">{t('home.decodeLabelDescription')}</div>
      </div>
      <div aria-hidden="true" className="text-xl">
        →
      </div>
    </Link>
  )
}

interface PendingTabletsCardProps {
  count: number
}

function PendingTabletsCard({ count }: PendingTabletsCardProps) {
  const { t } = useTranslation()
  return (
    <Link
      to="/tablets?status=pending"
      className="flex items-center gap-4 rounded-lg border border-amber-700 bg-amber-100 p-5 text-amber-950 shadow-sm transition hover:bg-amber-200"
    >
      <div
        aria-hidden="true"
        className="flex h-12 w-12 flex-none items-center justify-center rounded-md bg-amber-700 text-amber-50"
      >
        <TabletIcon />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-base font-semibold">{t('home.pendingTabletsTitle')}</div>
        <div className="text-sm text-amber-900/90">{t('home.pendingTabletsBody', { count })}</div>
      </div>
      <div aria-hidden="true" className="text-xl">
        →
      </div>
    </Link>
  )
}

function ScanQrIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-8 w-8"
      aria-hidden="true"
    >
      <path d="M4 8V5a1 1 0 0 1 1-1h3" />
      <path d="M20 8V5a1 1 0 0 0-1-1h-3" />
      <path d="M4 16v3a1 1 0 0 0 1 1h3" />
      <path d="M20 16v3a1 1 0 0 1-1 1h-3" />
      <rect x="9" y="9" width="2.5" height="2.5" rx="0.4" />
      <rect x="12.5" y="9" width="2.5" height="2.5" rx="0.4" />
      <rect x="9" y="12.5" width="2.5" height="2.5" rx="0.4" />
      <rect x="13.5" y="13.5" width="1.5" height="1.5" rx="0.3" />
    </svg>
  )
}

function TabletIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
      aria-hidden="true"
    >
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <circle cx="12" cy="18" r="0.9" fill="currentColor" />
    </svg>
  )
}
